/**
 * Shared utility for constructing entity-related URLs.
 */

/** Chart-minted custom entity refs (Dash-local D… ids). */
const LOCAL_ENTITY_REF_RE = /^D[1-9]\d*$/i;

const QID_RE = /^Q\d+$/i;

/** Product / FINTEL / report ids — never valid as chart entity refs. */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Whether an id is a locally minted chart entity ref (D…).
 * @param {string} id
 * @returns {boolean}
 */
export function isLocalEntityRef(id) {
    if (typeof id !== 'string') return false;
    const s = id.trim();
    return LOCAL_ENTITY_REF_RE.test(s);
}

/**
 * Whether a string looks like a product/FINTEL UUID (not a KB entity ref).
 * @param {string} id
 * @returns {boolean}
 */
export function looksLikeUuid(id) {
    return typeof id === 'string' && UUID_RE.test(id.trim());
}

/**
 * Whether an entity ref is linked to the KB (Wikidata QID or custom D ref).
 * @param {string} id
 * @returns {boolean}
 */
export function isLinkedEntityRef(id) {
    if (typeof id !== 'string') return false;
    const s = id.trim();
    return QID_RE.test(s) || isLocalEntityRef(s);
}

/**
 * Get the internal Knowledge Base URL for an entity.
 * @param {string} entityId - Firestore entity document ID
 * @returns {string} e.g. "/kb/abc123"
 */
export function getEntityKBUrl(entityId) {
    return `/kb/${entityId}`;
}

/**
 * Get the external Wikidata URL for an entity.
 * @param {string} wikidataId - Wikidata Q-number (e.g. "Q42")
 * @returns {string|null} e.g. "https://www.wikidata.org/wiki/Q42", or null if no ID
 */
export function getWikidataUrl(wikidataId) {
    if (!wikidataId || isLocalEntityRef(wikidataId)) return null;
    return `https://www.wikidata.org/wiki/${wikidataId}`;
}
